import { Grid } from '@mui/material';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { ActFormVM, LastStepVM, SectionVM } from 'civil-status/view-models/step-form';
import ActForm from './act-form';
import ActDigital from './act-digital';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { CodeTypeAct } from 'civil-status/constants/code-type-act';
import RadioGroupOptions from './radio-group-options';
import { ID_REGISTER } from 'civil-status/components/shared/list-register';
import { noop } from 'shell/utils/noop';

interface ActRelatedProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
  nothingRequired?: boolean;
  formContext: FormikProps<LastStepVM>;
  onDelete: () => void;
  idEvento?: number;
  idCat?: string;
  isLastStepEmergency?: boolean;
  onSave: () => void;
}

const ActRelatedComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  configuration,
  nothingRequired = false,
  formContext,
  onDelete,
  idEvento,
  idCat,
  isLastStepEmergency,
  onSave = noop,
}: ActRelatedProps): React.ReactElement => {
  const isAtcoBaseN = useMemo(() => {
    return configuration === ConfigurationType.ATCOBASEN;
  }, [configuration]);

  const isAtcoBaseD = useMemo(() => {
    return configuration === ConfigurationType.ATCOBASED;
  }, [configuration]);

  const isAtcoBaseDN = useMemo(() => {
    return configuration === ConfigurationType.ATCOBASEDN;
  }, [configuration]);

  const isAtcoBaseC = useMemo(() => {
    return configuration === ConfigurationType.ATCOBASEC;
  }, [configuration]);

  const isAtcoBaseCp: boolean = useMemo(() => {
    return configuration === ConfigurationType.ATCOBASECP;
  }, [configuration]);

  const isAtcoFa = useMemo(() => {
    return configuration === ConfigurationType.ATCOFA;
  }, [configuration]);

  const isAtcoFaN = useMemo(() => {
    return configuration === ConfigurationType.ATCOFAN;
  }, [configuration]);

  const isRactod343 = useMemo(() => {
    return configuration === ConfigurationType.RACTOD343;
  }, [configuration]);

  const isRactod344 = useMemo(() => {
    return configuration === ConfigurationType.RACTOD344;
  }, [configuration]);

  const isRactod341 = useMemo(() => {
    return configuration === ConfigurationType.RACTOD341;
  }, [configuration]);

  const isRactod0216 = useMemo(() => {
    return configuration === ConfigurationType.RACTOD0216;
  }, [configuration]);

  const isRactod0103 = useMemo(() => {
    return configuration === ConfigurationType.RACTOD0103;
  }, [configuration]);

  const isRactod441 = useMemo(() => {
    return configuration === ConfigurationType.RACTOD441;
  }, [configuration]);

  const isRactod350 = useMemo(() => {
    return configuration === ConfigurationType.RACTOD350;
  }, [configuration]);

  const isAtcoDRM = useMemo(() => {
    return configuration === ConfigurationType.ATCODRM;
  }, [configuration]);

  const isAtcoDRMA = useMemo(() => {
    return configuration === ConfigurationType.ATCODRMA;
  }, [configuration]);

  const isAtcoDRMM = useMemo(() => {
    return configuration === ConfigurationType.ATCODRMM;
  }, [configuration]);

  const isAtcoDRMU = useMemo(() => {
    return configuration === ConfigurationType.ATCODRMU;
  }, [configuration]);

  const onChangeAct = useCallback(
    (key, value) => {
      onChange('actForm', {
        ...(values || {}).actForm,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onChangesAct = useCallback(
    (valuesActForm) => {
      onChange('actForm', {
        ...(valuesActForm || {}),
      });
    },
    [values, onChange]
  );

  useEffect(() => {
    // in questi casi la configurazione dell'atto è già stabilita

    if (isAtcoBaseC && values?.typeAct !== CodeTypeAct.attoCartaceo) {
      onChange('typeAct', CodeTypeAct.attoCartaceo);
    }

    if (
      (isAtcoBaseD ||
        isAtcoBaseDN ||
        isRactod343 ||
        isRactod344 ||
        isRactod341 ||
        isRactod0216 ||
        isRactod0103 ||
        isRactod441 ||
        isRactod350 ||
        isAtcoDRM ||
        isAtcoDRMA ||
        isAtcoDRMM ||
        isAtcoDRMU) &&
      values?.typeAct !== CodeTypeAct.attoDigitale
    ) {
      onChange('typeAct', CodeTypeAct.attoDigitale);
    }
  }, [
    isAtcoBaseD,
    isAtcoBaseDN,
    isAtcoBaseC,
    isRactod343,
    isRactod344,
    isRactod341,
    isRactod0216,
    isRactod0103,
    isRactod441,
    isRactod350,
    isAtcoDRM,
    isAtcoDRMA,
    isAtcoDRMM,
    isAtcoDRMU,
    values?.typeAct,
  ]);

  useEffect(() => {
    if ((isAtcoBaseN || isAtcoFaN || isAtcoBaseDN) && !values?.actForm?.register) {
      onChangeAct('register', ID_REGISTER.NASCITA);
    }
  }, [isAtcoBaseN, isAtcoFaN, isAtcoBaseDN]);

  return (
    <Grid container spacing={2} item xs={12}>
      {isAtcoBaseD ||
      isAtcoBaseDN ||
      isAtcoBaseC ||
      isRactod343 ||
      isRactod344 ||
      isRactod341 ||
      isRactod0216 ||
      isRactod0103 ||
      isRactod441 ||
      isRactod350 ||
      isAtcoDRM ||
      isAtcoDRMA ||
      isAtcoDRMM ||
      isAtcoDRMU ? undefined : (
        <RadioGroupOptions
          onChanges={onChanges}
          disabled={disabled}
          isAtcoFa={isAtcoFa || isAtcoFaN}
          values={values}
          initialValues={formContext.initialValues}
        />
      )}
      {values?.typeAct === CodeTypeAct.attoCartaceo ? (
        <ActForm
          onChange={onChangeAct}
          onChanges={onChangesAct}
          values={values?.actForm}
          disabled={disabled}
          errors={errors?.actForm as FormikErrors<ActFormVM> | undefined}
          touched={touched?.actForm as FormikTouched<ActFormVM> | undefined}
          isPaper
          nothingRequired={nothingRequired}
          withPresentationDocument={isAtcoBaseCp}
        />
      ) : values?.typeAct === CodeTypeAct.attoDigitale ? (
        <ActDigital
          onChange={onChangeAct}
          onChanges={onChangesAct}
          onSave={onSave}
          values={values?.actForm}
          configuration={configuration}
          disabled={disabled}
          errors={errors?.actForm as FormikErrors<ActFormVM> | undefined}
          touched={touched?.actForm as FormikTouched<ActFormVM> | undefined}
          nothingRequired={nothingRequired}
          isLastStepEmergency={isLastStepEmergency}
          withPresentationDocument={isAtcoBaseCp}
          isRactod343={isRactod343}
          isRactod344={isRactod344}
          isRactod341={isRactod341}
          isRactod441={isRactod441}
          isRactod350={isRactod350}
          isRactod0216={isRactod0216}
          isRactod0103={isRactod0103}
          isAtcoDRM={isAtcoDRM}
          isAtcoDRMA={isAtcoDRMA}
          isAtcoDRMM={isAtcoDRMM}
          isAtcoDRMU={isAtcoDRMU}
          formContext={formContext}
          onDelete={onDelete}
          idEvento={idEvento}
          codeSection={values.codeSection}
          idCat={idCat}
        />
      ) : undefined}
    </Grid>
  );
};

ActRelatedComponent.displayName = 'Form Act Related component';

const ActRelated = memo(ActRelatedComponent);

export default ActRelated;
